// ─── Collage Element: RSVP Button ─────────────────────────────
// Composite canvas element: pill background + centered label.
//
// MVP model (per user-approved Phase 1 spec):
//   - Single layer with full styling vocabulary on the wrapper.
//   - element.content holds the translatable label (same shape as
//     `text_block`) — picked up automatically by the per-element
//     translation pipeline. No extra wiring required.
//   - On public click, navigates to `/invite/:slug/rsvp` (the dedicated
//     RSVP page). In Studio (isEditing=true) the click is a no-op so
//     designer selection isn't hijacked.
//
// Styling (element.customStyle):
//   bgColor, textColor, borderColor, borderWidth, borderRadius,
//   fontFamily, fontSize, fontWeight, letterSpacing, textTransform,
//   paddingX, paddingY, shadowStrength (0..100).

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';
import { useCollageElementMotion } from '../../../lib/utils/collageMotion';

export default function RsvpButtonElement({
  element,
  isMobile,
  theme,
  skipPositioning,
  isEditing,
  onEditText,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const slug = params?.slug;

  const cs = element.customStyle || {};
  const positionStyle = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const motionProps = useCollageElementMotion(element);

  // Visual defaults — kept minimal so the button inherits the theme.
  const accent = theme?.accent || theme?.textPrimary || '#1a1a1a';
  const onAccent = theme?.background || '#ffffff';
  const baseFill = cs.bgColor === 'transparent' ? 'transparent' : (cs.bgColor || 'transparent');
  // bgOpacity (0..100). When fill is "transparent" the slider is a no-op.
  // When fill is a real color, multiply alpha for visual fade.
  const bgOpacity = cs.bgOpacity === undefined ? 100 : Math.max(0, Math.min(100, Number(cs.bgOpacity)));
  const fill = (() => {
    if (baseFill === 'transparent' || !baseFill) return 'transparent';
    if (bgOpacity >= 100) return baseFill;
    // Convert hex → rgba(..., alpha).
    const hex = baseFill.replace('#', '');
    if (hex.length === 6 && /^[0-9a-fA-F]+$/.test(hex)) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${(bgOpacity / 100).toFixed(3)})`;
    }
    return baseFill;
  })();
  const textColor = cs.color || cs.textColor || accent;
  const borderColor = cs.borderColor || accent;
  const borderWidth = Number.isFinite(cs.borderWidth) ? cs.borderWidth : 1;
  const borderRadius = Number.isFinite(cs.borderRadius) ? cs.borderRadius : 999;
  const shadow = Math.max(0, Math.min(100, Number(cs.shadowStrength) || 0));
  const shadowStyle = shadow > 0
    ? `0 ${(4 + shadow * 0.2).toFixed(1)}px ${(20 + shadow * 0.4).toFixed(1)}px rgba(0,0,0,${(shadow * 0.0018).toFixed(3)})`
    : undefined;

  const handleClick = (e) => {
    // Studio editor: do nothing (selection is handled at the canvas
    // wrapper level). Public: navigate to the dedicated RSVP page.
    if (isEditing) return;
    e.preventDefault();
    e.stopPropagation();
    // Preserve query params (?preview=TOKEN, ?lang=…) so draft previews
    // and Share Preview links continue to work on the dedicated RSVP
    // page — otherwise the backend returns "Invitation not yet
    // published" on drafts.
    const search = window.location.search || '';
    const target = (s) => `/invite/${s}/rsvp${search}`;
    if (slug) {
      navigate(target(slug));
    } else {
      // Defensive fallback (should never hit on the public site).
      const m = window.location.pathname.match(/\/invite\/([^/]+)/);
      if (m && m[1]) navigate(target(m[1]));
    }
  };

  const handleKeyDown = (e) => {
    if (isEditing) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  const handleLabelBlur = (e) => {
    if (onEditText) onEditText('content', e.currentTarget.innerText);
  };

  const label = (element.content && String(element.content).trim()) || 'RSVP';

  const containerStyle = {
    ...positionStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: fill,
    color: textColor,
    border: `${borderWidth}px solid ${borderColor}`,
    borderRadius: `${borderRadius}px`,
    boxShadow: shadowStyle,
    paddingLeft: cs.paddingX !== undefined ? `${cs.paddingX}px` : '24px',
    paddingRight: cs.paddingX !== undefined ? `${cs.paddingX}px` : '24px',
    paddingTop: cs.paddingY !== undefined ? `${cs.paddingY}px` : '12px',
    paddingBottom: cs.paddingY !== undefined ? `${cs.paddingY}px` : '12px',
    // Optional explicit pixel height (designer-controlled). Overrides
    // padding-only sizing for a consistent tap target. Width still
    // comes from the canvas resize handles (element.layout.w).
    height: Number.isFinite(cs.heightPx) ? `${cs.heightPx}px` : undefined,
    minHeight: !Number.isFinite(cs.heightPx) ? '44px' : undefined,
    fontFamily: cs.fontFamily || theme?.bodyFont || undefined,
    fontSize: cs.fontSize || undefined,
    fontWeight: cs.fontWeight || 500,
    letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : '0.12em',
    textTransform: cs.textTransform || 'uppercase',
    cursor: isEditing ? 'default' : 'pointer',
    userSelect: isEditing ? 'text' : 'none',
    transition: 'transform 180ms ease, box-shadow 180ms ease',
  };
  if (!textColor || textColor === fill) {
    // Defensive — ensure contrast if a designer sets fill = accent.
    containerStyle.color = onAccent;
  }

  return (
    <motion.div
      {...motionProps}
      style={containerStyle}
      role={isEditing ? undefined : 'button'}
      tabIndex={isEditing ? undefined : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={isEditing ? undefined : `Open RSVP form — ${label}`}
      data-element-type="rsvp_button"
      data-element-name={label}
      data-testid={`rsvp-button-${element.id}`}
    >
      {isEditing ? (
        <span
          contentEditable
          suppressContentEditableWarning
          onBlur={handleLabelBlur}
          onKeyDown={(e) => { if (e.key === 'Escape') e.currentTarget.blur(); }}
          className="outline-none ring-1 ring-blue-400/50 rounded px-1 -mx-1"
          data-testid={`inline-edit-${element.id}`}
        >
          {label}
        </span>
      ) : (
        <span>{label}</span>
      )}
    </motion.div>
  );
}
